/**
 * RNG — provably-fair outcome generator.
 *
 * The scheme mirrors what real crash casinos publish so a player could verify
 * a round after the fact:
 *
 *   1. The house commits to a secret `serverSeed` by publishing its SHA-256
 *      hash BEFORE the round (the "commit"). It cannot change the seed later.
 *   2. The player supplies (and may edit) a `clientSeed`.
 *   3. Each lane's outcome is derived deterministically from
 *          HMAC-SHA256(serverSeed, `${clientSeed}:${nonce}:${lane}`)
 *      → the first 8 hex chars → a float in [0, 1).
 *   4. After the round the house reveals `serverSeed`; the player re-hashes it,
 *      checks it matches the earlier commit, and replays the HMAC to confirm
 *      every lane. Nothing was decided "live".
 *
 * All crypto uses the browser's native Web Crypto (SubtleCrypto) — no libs.
 */

const enc = new TextEncoder();

function toHex(buffer) {
  const bytes = new Uint8Array(buffer);
  let out = '';
  for (let i = 0; i < bytes.length; i++) out += bytes[i].toString(16).padStart(2, '0');
  return out;
}

/** SHA-256(message) → hex string. Used for the seed commitment. */
export async function sha256Hex(message) {
  const digest = await crypto.subtle.digest('SHA-256', enc.encode(message));
  return toHex(digest);
}

/** HMAC-SHA256(key, message) → hex string. */
async function hmacHex(key, message) {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    enc.encode(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const sig = await crypto.subtle.sign('HMAC', cryptoKey, enc.encode(message));
  return toHex(sig);
}

/** Cryptographically-strong random hex string of `bytes` length. */
export function randomSeed(bytes = 32) {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return toHex(buf.buffer);
}

/**
 * A provably-fair session: fixed serverSeed + clientSeed, incrementing nonce.
 * One instance lives for the whole play session; `nonce` bumps every round so
 * identical seeds still produce independent rounds.
 */
export default class RNG {
  constructor(clientSeed) {
    this.serverSeed = randomSeed(32);
    this.clientSeed = clientSeed || randomSeed(8);
    this.nonce = 0;
    this._commit = null; // sha256(serverSeed), computed lazily
  }

  /** The public commitment hash for the current serverSeed. */
  async commitment() {
    if (!this._commit) this._commit = await sha256Hex(this.serverSeed);
    return this._commit;
  }

  /** Rotate to a fresh serverSeed (e.g. after the player edits the client seed). */
  async reseed(clientSeed) {
    this.serverSeed = randomSeed(32);
    if (clientSeed !== undefined) this.clientSeed = clientSeed;
    this._commit = null;
    this.nonce = 0;
    return this.commitment();
  }

  /**
   * Deterministic float in [0,1) for a single lane of the current round.
   * @param {number} lane 0-based lane index
   */
  async laneFloat(lane) {
    const msg = `${this.clientSeed}:${this.nonce}:${lane}`;
    const hex = await hmacHex(this.serverSeed, msg);
    // Use the leading 52 bits (13 hex chars) for a full double-precision float.
    const slice = hex.slice(0, 13);
    return parseInt(slice, 16) / 0x10000000000000; // 2^52
  }

  /**
   * Pre-compute every lane's death verdict for one round and return the index
   * of the FIRST deadly lane (or Infinity if the whole road is safe).
   *
   * @param {number} laneCount number of crossable lanes
   * @param {(lane:number) => number} probFn conditional death probability for
   *        stepping onto 1-based lane `k`, given every earlier lane was safe
   * @returns {Promise<{deathLane:number, floats:number[], meta:object}>}
   */
  async rollRound(laneCount, probFn) {
    const floats = [];
    let deathLane = Infinity;
    for (let lane = 1; lane <= laneCount; lane++) {
      const f = await this.laneFloat(lane);
      floats.push(f);
      if (deathLane === Infinity && f < probFn(lane)) deathLane = lane;
    }
    const meta = {
      serverSeed: this.serverSeed,
      clientSeed: this.clientSeed,
      nonce: this.nonce,
      commitment: await this.commitment(),
    };
    this.nonce += 1; // consume this nonce; next round is independent
    return { deathLane, floats, meta };
  }
}
